import { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
  toast,
} from '@kognitos/lattice';
import { TriggerRow } from './TriggerRow';
import { TriggerType } from './TriggerType';
import {
  WebhookTriggerDialog,
  convertConditionsToExpression,
  convertExpressionToConditions,
  type IWebhookTriggerSaveData,
  type IWebhookTriggerInitialData,
  type TInputMappings,
  type ITriggerFieldGroup,
  type TConfigurationValues,
  type IFilterCondition,
} from '../WebhookTriggerDialog';
import {
  EmailTriggerDialog,
  type IEmailTriggerSaveData,
  type IEmailTriggerInitialData,
  type IEmailFilters,
} from '../EmailTriggerDialog';
import { PauseTriggerDialog } from '../PauseTriggerDialog';
import { ResumeTriggerDialog } from '../ResumeTriggerDialog';
import { DeleteTriggerDialog } from '../DeleteTriggerDialog';
import { PollTriggerDialog } from '../PollTriggerDialog/PollTriggerDialog';
import type {
  IPollTriggerSaveData,
  IPollTriggerInitialData,
} from '../PollTriggerDialog/types';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import { BOOK_CATALOG_STALE_TIME } from '@/modules/integrations/constants';
import { generateMailboxAddress } from '../../utils/email-utils';
import {
  convertExpressionToFilters,
  createEmailTriggerConfigurationAndFilters,
  createPollTriggerConfiguration,
  createWebhookTriggerConfiguration,
  parseEmailTriggerConfiguration,
  parsePollTriggerConfiguration,
  parseWebhookTriggerConfiguration,
} from './utils';
import {
  getTriggerStatus,
  TRIGGER_TYPE,
} from '../../utils/trigger-status-utils';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import {
  useRetrieveWorkspaceBook,
  useRetrieveWorkspaceBookConnection,
  useUpdateWorkspaceBookConnectionTriggerInstance,
  useUpdateWorkspaceBookConnectionPollInstance,
  getRetrieveWorkspaceTriggerInstancesQueryKey,
  getRetrieveWorkspacePollInstancesQueryKey,
} from '@/generated/hooks/bdk/v2/services/library';
import type { V2PollInstance } from '@/generated/hooks/bdk/models';
import {
  CreateAutomationTriggerStage,
  V1PhaseType,
} from '@/generated/hooks/grimoire/models';
import {
  useCreateAutomationTrigger,
  getListAutomationTriggersQueryKey,
} from '@/generated/hooks/grimoire/v1/automation_phase';
import { captureException } from '@/modules/sentry';
import type { IExistingTriggerProps } from './types';

/**
 * ExistingTrigger Component
 *
 * Displays a single existing trigger with information from BDK trigger instance.
 * Fetches integration and connection details to show icon, name, connection name, and status.
 * Uses Grimoire trigger info passed from parent for displaying input mappings in the dialog.
 * Clicking on the trigger opens the WebhookTriggerDialog in edit/readOnly mode.
 *
 * @param props - Component props
 * @returns JSX element displaying the trigger
 */
export function ExistingTrigger({
  isDraft,
  triggerInstance,
  grimoireTriggerInfo,
  inputSpecs,
  automationId,
  isReadOnly = false,
  isSelected = false,
  presentation = 'card',
}: IExistingTriggerProps) {
  const { t } = useTranslation('automation-inputs');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const queryClient = useQueryClient();

  // Poll instances expose `poll_id` and `poll_interval_minutes` while webhook
  // trigger instances expose `trigger_id` / `endpoint`. The guard is the single
  // source of truth for branching: dialog selection, initial data, pause/resume
  // copy. `poll_interval_minutes` is used as the discriminant rather than
  // `poll_id` because it has no falsy-but-valid value (unlike an empty-string
  // poll_id), so it can't be mistaken for a missing field.
  const isPoll =
    'poll_interval_minutes' in triggerInstance &&
    triggerInstance.poll_interval_minutes !== undefined;
  const pollInstance = isPoll ? (triggerInstance as V2PollInstance) : undefined;
  const triggerInstanceTriggerId =
    'trigger_id' in triggerInstance ? triggerInstance.trigger_id : undefined;

  // Dialog state
  const {
    value: isDialogOpen,
    setTrue: openDialog,
    setValue: setIsDialogOpen,
  } = useBoolean(false);

  // Pause confirmation dialog state
  const {
    value: isPauseDialogOpen,
    setTrue: openPauseDialog,
    setFalse: closePauseDialog,
  } = useBoolean(false);

  // Resume confirmation dialog state
  const {
    value: isResumeDialogOpen,
    setTrue: openResumeDialog,
    setFalse: closeResumeDialog,
  } = useBoolean(false);

  // Delete confirmation dialog state
  const {
    value: isDeleteDialogOpen,
    setTrue: openDeleteDialog,
    setFalse: closeDeleteDialog,
  } = useBoolean(false);

  // Controlled state for email dialog - initialized from trigger data
  const emailInitialFilters = useMemo<IEmailFilters>(
    () => convertExpressionToFilters(triggerInstance.filter_expression),
    [triggerInstance.filter_expression],
  );
  const emailInitialMappings = useMemo<TInputMappings>(
    () => grimoireTriggerInfo.input_mapping ?? {},
    [grimoireTriggerInfo.input_mapping],
  );
  const [emailCurrentStep, setEmailCurrentStep] = useState(1);
  const [emailFilters, setEmailFilters] =
    useState<IEmailFilters>(emailInitialFilters);
  const [emailInputMappings, setEmailInputMappings] =
    useState<TInputMappings>(emailInitialMappings);

  // Keep controlled dialog state in sync with latest server data when the dialog is closed.
  // Without this, after a successful update + query refetch, the UI can continue to show
  // stale filter/mapping state until a full page refresh.
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync controlled dialog state from server data after refetch
  useEffect(() => {
    if (!isDialogOpen) {
      setEmailFilters(emailInitialFilters);
      setEmailInputMappings(emailInitialMappings);
    }
  }, [isDialogOpen, emailInitialFilters, emailInitialMappings]);

  /**
   * Resets email dialog state to initial values
   */
  const resetEmailDialogState = useCallback(() => {
    setEmailCurrentStep(1);
    setEmailFilters(emailInitialFilters);
    setEmailInputMappings(emailInitialMappings);
  }, [emailInitialFilters, emailInitialMappings]);

  /**
   * Handles dialog open state changes.
   * Resets email state when dialog closes.
   */
  const handleEmailDialogOpenChange = useCallback(
    (open: boolean) => {
      setIsDialogOpen(open);
      if (!open) {
        resetEmailDialogState();
      }
    },
    [setIsDialogOpen, resetEmailDialogState],
  );

  // Fetch connection details from BDK first (needed for book_version)
  const { data: connectionData, isLoading: isLoadingConnection } =
    useRetrieveWorkspaceBookConnection(
      organizationId,
      workspaceId,
      triggerInstance.connection_id || '',
      {
        query: {
          enabled: Boolean(
            organizationId && workspaceId && triggerInstance.connection_id,
          ),
        },
      },
    );

  const connection = connectionData?.data?.book_connection;

  // Controlled state for webhook dialog - initialized from trigger data
  const webhookInitialMappings = useMemo<TInputMappings>(
    () => grimoireTriggerInfo.input_mapping ?? {},
    [grimoireTriggerInfo.input_mapping],
  );
  const webhookInitialConfigValues = useMemo<TConfigurationValues>(
    () => parseWebhookTriggerConfiguration(triggerInstance.configuration),
    [triggerInstance.configuration],
  );
  const webhookInitialFilterConditions = useMemo<IFilterCondition[]>(
    () => convertExpressionToConditions(triggerInstance.filter_expression),
    [triggerInstance.filter_expression],
  );
  const [webhookCurrentStep, setWebhookCurrentStep] = useState(1);
  // Initialize from trigger instance data directly
  const [webhookSelectedIntegration, setWebhookSelectedIntegration] = useState<
    string | undefined
  >(triggerInstance.book_name);
  const [
    webhookSelectedIntegrationVersion,
    setWebhookSelectedIntegrationVersion,
  ] = useState<string | undefined>(undefined); // Will be set when connection loads
  const [webhookSelectedConnection, setWebhookSelectedConnection] = useState<
    string | undefined
  >(triggerInstance.connection_id);
  const [webhookSelectedEvent, setWebhookSelectedEvent] = useState<
    string | undefined
  >(triggerInstanceTriggerId);
  const [webhookInputMappings, setWebhookInputMappings] =
    useState<TInputMappings>(webhookInitialMappings);
  const [webhookTriggerFieldGroups, setWebhookTriggerFieldGroups] = useState<
    ITriggerFieldGroup[]
  >([]);
  const [webhookConfigurationValues, setWebhookConfigurationValues] =
    useState<TConfigurationValues>(webhookInitialConfigValues);
  const [webhookFilterConditions, setWebhookFilterConditions] = useState<
    IFilterCondition[]
  >(webhookInitialFilterConditions);

  // Keep controlled dialog state in sync with latest server data when the dialog is closed.
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync controlled dialog state from server data after refetch
  useEffect(() => {
    if (!isDialogOpen) {
      setWebhookSelectedIntegration(triggerInstance.book_name);
      setWebhookSelectedConnection(triggerInstance.connection_id);
      setWebhookSelectedEvent(triggerInstanceTriggerId);
      setWebhookInputMappings(webhookInitialMappings);
      setWebhookConfigurationValues(webhookInitialConfigValues);
      setWebhookFilterConditions(webhookInitialFilterConditions);
    }
  }, [
    isDialogOpen,
    triggerInstance.book_name,
    triggerInstance.connection_id,
    triggerInstanceTriggerId,
    webhookInitialMappings,
    webhookInitialConfigValues,
    webhookInitialFilterConditions,
  ]);

  // Sync version when connection data loads (one-time sync for initial data)
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync version from async connection data on initial load
  useEffect(() => {
    if (connection?.book_version && !webhookSelectedIntegrationVersion) {
      setWebhookSelectedIntegrationVersion(connection.book_version);
    }
  }, [connection?.book_version, webhookSelectedIntegrationVersion]);

  // Controlled state for poll dialog - initialized from poll instance data
  const pollInitialMappings = useMemo<TInputMappings>(
    () => grimoireTriggerInfo.input_mapping ?? {},
    [grimoireTriggerInfo.input_mapping],
  );
  const pollInitialConfigValues = useMemo<TConfigurationValues>(
    () => parsePollTriggerConfiguration(pollInstance?.configuration),
    [pollInstance?.configuration],
  );
  const [pollCurrentStep, setPollCurrentStep] = useState(1);
  const [pollSelectedIntegration, setPollSelectedIntegration] = useState<
    string | undefined
  >(pollInstance?.book_name);
  const [pollSelectedIntegrationVersion, setPollSelectedIntegrationVersion] =
    useState<string | undefined>(undefined);
  const [pollSelectedConnection, setPollSelectedConnection] = useState<
    string | undefined
  >(pollInstance?.connection_id);
  const [pollSelectedPollId, setPollSelectedPollId] = useState<
    string | undefined
  >(pollInstance?.poll_id);
  const [pollSelectedPollIntervalMinutes, setPollSelectedPollIntervalMinutes] =
    useState<number | undefined>(pollInstance?.poll_interval_minutes);
  const [pollInputMappings, setPollInputMappings] =
    useState<TInputMappings>(pollInitialMappings);
  const [pollTriggerFieldGroups, setPollTriggerFieldGroups] = useState<
    ITriggerFieldGroup[]
  >([]);
  const [pollConfigurationValues, setPollConfigurationValues] =
    useState<TConfigurationValues>(pollInitialConfigValues);

  // Keep controlled poll dialog state in sync with latest server data when the
  // dialog is closed (mirrors the webhook block).
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync controlled dialog state from server data after refetch
  useEffect(() => {
    if (!isDialogOpen) {
      setPollSelectedIntegration(pollInstance?.book_name);
      setPollSelectedConnection(pollInstance?.connection_id);
      setPollSelectedPollId(pollInstance?.poll_id);
      setPollSelectedPollIntervalMinutes(pollInstance?.poll_interval_minutes);
      setPollInputMappings(pollInitialMappings);
      setPollConfigurationValues(pollInitialConfigValues);
    }
  }, [
    isDialogOpen,
    pollInstance?.book_name,
    pollInstance?.connection_id,
    pollInstance?.poll_id,
    pollInstance?.poll_interval_minutes,
    pollInitialMappings,
    pollInitialConfigValues,
  ]);

  // Sync poll version when connection data loads (one-time sync for initial data)
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync version from async connection data on initial load
  useEffect(() => {
    if (connection?.book_version && !pollSelectedIntegrationVersion) {
      setPollSelectedIntegrationVersion(connection.book_version);
    }
  }, [connection?.book_version, pollSelectedIntegrationVersion]);

  // The trigger list is a join of the Grimoire trigger records and the BDK
  // instances, so both halves have to be refreshed. Delete tears down the BDK
  // instance and pause/resume change its status, and the row reads status,
  // poll interval and endpoint from the BDK side.
  const invalidateTriggerQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getListAutomationTriggersQueryKey(
        organizationId,
        workspaceId,
        automationId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getRetrieveWorkspaceTriggerInstancesQueryKey(
        organizationId,
        workspaceId,
      ),
    });
    queryClient.invalidateQueries({
      queryKey: getRetrieveWorkspacePollInstancesQueryKey(
        organizationId,
        workspaceId,
      ),
    });
  }, [queryClient, organizationId, workspaceId, automationId]);

  /**
   * Resets webhook dialog state to initial values
   */
  const resetWebhookDialogState = useCallback(() => {
    setWebhookCurrentStep(1);
    setWebhookSelectedIntegration(triggerInstance.book_name);
    setWebhookSelectedIntegrationVersion(connection?.book_version);
    setWebhookSelectedConnection(triggerInstance.connection_id);
    setWebhookSelectedEvent(triggerInstanceTriggerId);
    setWebhookInputMappings(webhookInitialMappings);
    setWebhookTriggerFieldGroups([]);
    setWebhookConfigurationValues(webhookInitialConfigValues);
    setWebhookFilterConditions(webhookInitialFilterConditions);
  }, [
    triggerInstance.book_name,
    connection?.book_version,
    triggerInstance.connection_id,
    triggerInstanceTriggerId,
    webhookInitialMappings,
    webhookInitialConfigValues,
    webhookInitialFilterConditions,
  ]);

  /**
   * Handles webhook dialog open state changes.
   * Resets webhook state when dialog closes.
   */
  const handleWebhookDialogOpenChange = useCallback(
    (open: boolean) => {
      setIsDialogOpen(open);
      if (!open) {
        resetWebhookDialogState();
      }
    },
    [setIsDialogOpen, resetWebhookDialogState],
  );

  /**
   * Resets poll dialog state to initial values
   */
  const resetPollDialogState = useCallback(() => {
    setPollCurrentStep(1);
    setPollSelectedIntegration(pollInstance?.book_name);
    setPollSelectedIntegrationVersion(connection?.book_version);
    setPollSelectedConnection(pollInstance?.connection_id);
    setPollSelectedPollId(pollInstance?.poll_id);
    setPollSelectedPollIntervalMinutes(pollInstance?.poll_interval_minutes);
    setPollInputMappings(pollInitialMappings);
    setPollConfigurationValues(pollInitialConfigValues);
  }, [
    pollInstance?.book_name,
    connection?.book_version,
    pollInstance?.connection_id,
    pollInstance?.poll_id,
    pollInstance?.poll_interval_minutes,
    pollInitialMappings,
    pollInitialConfigValues,
  ]);

  /**
   * Handles poll dialog open state changes.
   * Resets poll state when dialog closes.
   */
  const handlePollDialogOpenChange = useCallback(
    (open: boolean) => {
      setIsDialogOpen(open);
      if (!open) {
        resetPollDialogState();
      }
    },
    [setIsDialogOpen, resetPollDialogState],
  );

  // Mutation for updating the trigger (using create as upsert)
  const { mutateAsync: updateAutomationTrigger, isPending: isUpdating } =
    useCreateAutomationTrigger();

  // BDK mutation for updating the trigger instance configuration/filtering
  const {
    mutateAsync: updateTriggerInstance,
    isPending: isUpdatingTriggerInstance,
  } = useUpdateWorkspaceBookConnectionTriggerInstance();

  // BDK mutation for updating the poll instance
  const { mutateAsync: updatePollInstance, isPending: isUpdatingPollInstance } =
    useUpdateWorkspaceBookConnectionPollInstance();

  // Fetch integration details using connection's book name and version.
  // A book descriptor changes only when the integration is reinstalled, and
  // sibling triggers on the same page usually share one, so cache it rather
  // than refetching per card per mount.
  const { data: appData, isLoading: isLoadingApp } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    connection?.book_name || '',
    connection?.book_version || '',
    {
      query: {
        enabled: Boolean(
          organizationId &&
            workspaceId &&
            connection?.book_name &&
            connection?.book_version,
        ),
        staleTime: BOOK_CATALOG_STALE_TIME,
      },
    },
  );

  const integration = appData?.data?.book;

  // Determine status from trigger instance state
  const triggerStatus = getTriggerStatus(triggerInstance, grimoireTriggerInfo);

  /**
   * Prepares initial data for the webhook edit dialog from existing trigger data
   */
  const getWebhookInitialData = useCallback((): IWebhookTriggerInitialData => {
    // Parse configuration from trigger instance to TConfigurationValues
    const configurationValues = parseWebhookTriggerConfiguration(
      triggerInstance.configuration,
    );

    return {
      selectedIntegration: connection?.book_name,
      selectedIntegrationVersion: connection?.book_version,
      selectedConnection: triggerInstance.connection_id,
      selectedEvent: triggerInstanceTriggerId,
      inputMappings: grimoireTriggerInfo.input_mapping ?? {},
      // Trigger field groups will be extracted when the event is loaded in the dialog
      triggerFieldGroups: [],
      configurationValues,
      filterConditions: webhookInitialFilterConditions,
    };
  }, [
    connection,
    triggerInstance,
    triggerInstanceTriggerId,
    grimoireTriggerInfo.input_mapping,
    webhookInitialFilterConditions,
  ]);

  // Parse email address from backend configuration
  const emailAddressFromBackend = useMemo(
    () => parseEmailTriggerConfiguration(triggerInstance.configuration),
    [triggerInstance.configuration],
  );

  /**
   * Prepares initial data for the email edit dialog from existing trigger data
   */
  const getEmailInitialData = useCallback((): IEmailTriggerInitialData => {
    // Convert filter_expression from trigger instance to IEmailFilters
    const filters = convertExpressionToFilters(
      triggerInstance.filter_expression,
    );

    return {
      // Use email address from backend for existing triggers
      mailboxAddress:
        emailAddressFromBackend ||
        generateMailboxAddress(automationId, isDraft),
      filters,
      inputMappings: grimoireTriggerInfo.input_mapping ?? {},
      // Trigger field groups will be extracted when the email book is loaded in the dialog
      triggerFieldGroups: [],
    };
  }, [
    automationId,
    emailAddressFromBackend,
    grimoireTriggerInfo.input_mapping,
    isDraft,
    triggerInstance.filter_expression,
  ]);

  /**
   * Handles webhook trigger update from the dialog
   */
  const handleWebhookUpdate = useCallback(
    async (data: IWebhookTriggerSaveData) => {
      if (
        !organizationId ||
        !workspaceId ||
        !triggerInstance.id ||
        !triggerInstance.connection_id
      ) {
        toast.error(t('webhookDialog.toast.error'));
        return;
      }

      try {
        // 1) Update BDK trigger instance configuration. The BDK PUT endpoint
        // can only mutate name / configuration / filter_expression — connection
        // and trigger identity are pinned to the URL, which is why the dialog
        // keeps those selectors locked in edit mode.
        const configuration = createWebhookTriggerConfiguration(
          data.configurationValues,
        );

        const filter_expression = data.filterCapable
          ? convertConditionsToExpression(data.filterConditions)
          : triggerInstance.filter_expression;

        await updateTriggerInstance({
          organizationId,
          workspaceId,
          connectionId: triggerInstance.connection_id,
          triggerInstanceId: triggerInstance.id,
          data: {
            name: triggerInstance.name || triggerInstance.id,
            configuration,
            filter_expression,
          },
        });

        // 2) Update Grimoire automation trigger with new input mappings.
        // The stage query param scopes the upsert to the draft or published
        // version of the automation.
        await updateAutomationTrigger({
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {
            trigger_id: triggerInstance.id,
            input_mapping: data.inputMappings,
            phase_type: V1PhaseType.PHASE_TYPE_PRE,
          },
          params: {
            stage: isDraft
              ? CreateAutomationTriggerStage.AUTOMATION_STAGE_DRAFT
              : CreateAutomationTriggerStage.AUTOMATION_STAGE_PUBLISHED,
          },
        });

        // Invalidate + refetch queries to refresh the list.
        // NOTE: This component uses controlled local state, so we want fresh server
        // data in the cache before closing the dialog to avoid showing stale values
        // until a full page refresh.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getListAutomationTriggersQueryKey(
              organizationId,
              workspaceId,
              automationId,
            ),
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: getRetrieveWorkspaceTriggerInstancesQueryKey(
              organizationId,
              workspaceId,
            ),
            exact: false,
          }),
        ]);

        toast.success(t('webhookDialog.toast.updateSuccess'));
        handleWebhookDialogOpenChange(false);
      } catch (error) {
        captureException(error, {
          tags: {
            component: 'ExistingTrigger',
            action: 'handleWebhookUpdate',
          },
        });
        toast.error(t('webhookDialog.toast.updateError'));
      }
    },
    [
      organizationId,
      workspaceId,
      automationId,
      isDraft,
      triggerInstance.id,
      triggerInstance.connection_id,
      triggerInstance.name,
      triggerInstance.filter_expression,
      updateTriggerInstance,
      updateAutomationTrigger,
      queryClient,
      handleWebhookDialogOpenChange,
      t,
    ],
  );

  /**
   * Handles email trigger update from the dialog
   */
  const handleEmailUpdate = useCallback(
    async (data: IEmailTriggerSaveData) => {
      if (
        !organizationId ||
        !workspaceId ||
        !triggerInstance.id ||
        !triggerInstance.connection_id
      ) {
        toast.error(t('emailDialog.toast.error'));
        return;
      }

      try {
        // 1) Update BDK trigger instance filtering/configuration (Email Filters)
        // PUT semantics: include required fields
        const { configuration, filter_expression } =
          createEmailTriggerConfigurationAndFilters(
            data.mailboxAddress,
            data.filters,
          );

        await updateTriggerInstance({
          organizationId,
          workspaceId,
          connectionId: triggerInstance.connection_id,
          triggerInstanceId: triggerInstance.id,
          data: {
            name: triggerInstance.name || triggerInstance.id,
            configuration,
            filter_expression,
          },
        });

        // 2) Update Grimoire automation trigger with new input mappings.
        // The stage query param scopes the upsert to the draft or published
        // version of the automation.
        await updateAutomationTrigger({
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {
            trigger_id: triggerInstance.id,
            input_mapping: data.inputMappings,
            phase_type: V1PhaseType.PHASE_TYPE_PRE,
          },
          params: {
            stage: isDraft
              ? CreateAutomationTriggerStage.AUTOMATION_STAGE_DRAFT
              : CreateAutomationTriggerStage.AUTOMATION_STAGE_PUBLISHED,
          },
        });

        // Invalidate + refetch queries to refresh the list.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getListAutomationTriggersQueryKey(
              organizationId,
              workspaceId,
              automationId,
            ),
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: getRetrieveWorkspaceTriggerInstancesQueryKey(
              organizationId,
              workspaceId,
            ),
            exact: false,
          }),
        ]);

        toast.success(t('emailDialog.toast.updateSuccess'));
        handleEmailDialogOpenChange(false);
      } catch (error) {
        captureException(error, {
          tags: {
            component: 'ExistingTrigger',
            action: 'handleEmailUpdate',
          },
        });
        toast.error(t('emailDialog.toast.updateError'));
      }
    },
    [
      organizationId,
      workspaceId,
      automationId,
      isDraft,
      triggerInstance.id,
      triggerInstance.connection_id,
      triggerInstance.name,
      updateTriggerInstance,
      updateAutomationTrigger,
      queryClient,
      handleEmailDialogOpenChange,
      t,
    ],
  );

  /**
   * Prepares initial data for the poll edit dialog from existing poll instance data
   */
  const getPollInitialData = useCallback((): IPollTriggerInitialData => {
    const configurationValues = parsePollTriggerConfiguration(
      pollInstance?.configuration,
    );

    return {
      selectedIntegration: connection?.book_name,
      selectedIntegrationVersion: connection?.book_version,
      selectedConnection: pollInstance?.connection_id,
      selectedPollId: pollInstance?.poll_id,
      selectedPollIntervalMinutes: pollInstance?.poll_interval_minutes,
      inputMappings: grimoireTriggerInfo.input_mapping ?? {},
      configurationValues,
    };
  }, [connection, pollInstance, grimoireTriggerInfo.input_mapping]);

  /**
   * Handles poll trigger update from the dialog
   */
  const handlePollUpdate = useCallback(
    async (data: IPollTriggerSaveData) => {
      if (
        !organizationId ||
        !workspaceId ||
        !triggerInstance.id ||
        !triggerInstance.connection_id
      ) {
        toast.error(t('pollDialog.toast.error'));
        return;
      }

      try {
        const configuration = createPollTriggerConfiguration(
          data.configurationValues,
        );

        await updatePollInstance({
          organizationId,
          workspaceId,
          connectionId: triggerInstance.connection_id,
          pollInstanceId: triggerInstance.id,
          data: {
            name: triggerInstance.name || triggerInstance.id,
            configuration,
            filter_expression: triggerInstance.filter_expression,
            poll_interval_minutes: data.selectedPollIntervalMinutes,
          },
        });

        await updateAutomationTrigger({
          organization: organizationId,
          workspace: workspaceId,
          automation: automationId,
          data: {
            trigger_id: triggerInstance.id,
            input_mapping: data.inputMappings,
            phase_type: V1PhaseType.PHASE_TYPE_PRE,
          },
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getListAutomationTriggersQueryKey(
              organizationId,
              workspaceId,
              automationId,
            ),
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: getRetrieveWorkspacePollInstancesQueryKey(
              organizationId,
              workspaceId,
            ),
            exact: false,
          }),
        ]);

        toast.success(t('pollDialog.toast.updateSuccess'));
        handlePollDialogOpenChange(false);
      } catch (error) {
        captureException(error, {
          tags: {
            component: 'ExistingTrigger',
            action: 'handlePollUpdate',
          },
        });
        toast.error(t('pollDialog.toast.updateError'));
      }
    },
    [
      organizationId,
      workspaceId,
      automationId,
      triggerInstance.id,
      triggerInstance.connection_id,
      triggerInstance.name,
      triggerInstance.filter_expression,
      updatePollInstance,
      updateAutomationTrigger,
      queryClient,
      handlePollDialogOpenChange,
      t,
    ],
  );

  // The connection is always required: it decides the trigger type, label and
  // status, so rendering without it would flip a row from "Webhook" to "Email"
  // under the user.
  //
  // The book behind it is a second, serial round trip, and only the row
  // presentation can render without it — `rowTitle` is derived entirely from
  // the instance and connection, so the only thing still missing is the branded
  // icon, which has a generic fallback. The card presentation renders `title`,
  // which prefers the book's `display_name`, so it would visibly swap its
  // heading; it keeps waiting.
  if (isLoadingConnection || (presentation !== 'row' && isLoadingApp)) {
    return (
      <Skeleton
        className={
          presentation === 'row'
            ? 'h-[70px] min-w-0 w-full max-w-full rounded-xl'
            : 'h-40 w-35 shrink-0 rounded-lg'
        }
        data-testid="existing-trigger-skeleton"
      />
    );
  }

  // Check if this is an email trigger
  const isEmailTrigger = connection?.book_name?.toLowerCase() === 'email';

  const triggerTypeLabel = isEmailTrigger
    ? t('triggerTypes.email.label')
    : isPoll
      ? t('triggerTypes.poll.label', {
          minutes: pollInstance?.poll_interval_minutes,
        })
      : t('triggerTypes.webhook.label');

  // Integration display name, falling back to the instance name and then to the
  // trigger type. The instance id is deliberately not a fallback — an unnamed
  // trigger on an integration with no display name would otherwise show a raw
  // uuid as its heading.
  const title =
    integration?.display_name || triggerInstance.name || triggerTypeLabel;

  // For email triggers, show email address from backend; otherwise show connection name
  const description = isEmailTrigger
    ? emailAddressFromBackend || generateMailboxAddress(automationId, isDraft)
    : connection?.connection_name || '';

  // Render appropriate icon based on trigger type
  const iconElement = isEmailTrigger ? (
    <Icon type="Mail" size="md" className="text-muted-foreground" />
  ) : integration?.icon ? (
    <img
      src={convertToDataUrl(integration.icon)}
      alt={integration.display_name || integration.name || ''}
      className="h-5 w-5 object-contain"
    />
  ) : isPoll ? (
    <Icon type="RefreshCw" size="md" className="text-muted-foreground" />
  ) : (
    <Icon type="Webhook" size="md" className="text-muted-foreground" />
  );

  /**
   * Handles toggling the active state of the trigger.
   * Opens the appropriate confirmation dialog (pause or resume).
   */
  const handleToggleActiveState = () => {
    if (!grimoireTriggerInfo.trigger_id) return;

    if (grimoireTriggerInfo.is_active) {
      // Active trigger -> Open pause confirmation dialog
      openPauseDialog();
    } else {
      // Paused trigger -> Open resume confirmation dialog
      openResumeDialog();
    }
  };

  /**
   * Opens the delete confirmation dialog for the trigger.
   */
  const handleDeleteTrigger = () => {
    if (!grimoireTriggerInfo.trigger_id) return;
    openDeleteDialog();
  };

  /**
   * Handles a successful trigger deletion: refresh the trigger list and close
   * the editor dialog since the trigger no longer exists.
   */
  const handleDeleteSuccess = () => {
    invalidateTriggerQueries();
    setIsDialogOpen(false);
  };

  // Deliberately does not consult `title`: the row renders before the book
  // request lands, and `title` prefers the book's display name, so including it
  // here would make the heading swap mid-load.
  const rowTitle = isEmailTrigger
    ? description
    : triggerInstance.name || description || triggerTypeLabel;

  return (
    <>
      {presentation === 'row' ? (
        <TriggerRow
          icon={iconElement}
          typeLabel={triggerTypeLabel}
          title={rowTitle}
          status={triggerStatus}
          onOpen={openDialog}
          menu={
            !isReadOnly && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('trigger.actions.menu')}
                    data-testid="trigger-actions-menu"
                    className="size-8 shrink-0 !bg-transparent hover:!bg-transparent"
                  >
                    <Icon type="EllipsisVertical" size="sm" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onSelect={openDialog}>
                    <Icon type="PencilLine" size="sm" />
                    {t('trigger.actions.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleToggleActiveState}>
                    <Icon
                      type={grimoireTriggerInfo.is_active ? 'Pause' : 'Play'}
                      size="sm"
                    />
                    {grimoireTriggerInfo.is_active
                      ? t('trigger.actions.pauseTrigger')
                      : t('trigger.actions.resume')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={handleDeleteTrigger}
                  >
                    <Icon type="Trash" size="sm" />
                    {t('deleteDialog.deleteTrigger')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )
          }
        />
      ) : (
        <TriggerType
          icon={iconElement}
          title={title}
          description={description}
          status={triggerStatus}
          onClick={openDialog}
          isSelected={isSelected}
        />
      )}

      {isPoll ? (
        <PollTriggerDialog
          isOpen={isDialogOpen}
          onOpenChange={handlePollDialogOpenChange}
          inputSpecs={inputSpecs}
          isLoading={isUpdating || isUpdatingPollInstance}
          mode={isReadOnly ? 'readOnly' : 'edit'}
          initialData={getPollInitialData()}
          triggerName={title}
          triggerStatus={triggerStatus}
          onUpdate={handlePollUpdate}
          // Controlled state props
          currentStep={pollCurrentStep}
          onStepChange={setPollCurrentStep}
          selectedIntegration={pollSelectedIntegration}
          onSelectedIntegrationChange={setPollSelectedIntegration}
          selectedIntegrationVersion={pollSelectedIntegrationVersion}
          onSelectedIntegrationVersionChange={setPollSelectedIntegrationVersion}
          selectedConnection={pollSelectedConnection}
          onSelectedConnectionChange={setPollSelectedConnection}
          selectedPollId={pollSelectedPollId}
          onSelectedPollChange={setPollSelectedPollId}
          selectedPollIntervalMinutes={pollSelectedPollIntervalMinutes}
          onSelectedPollIntervalMinutesChange={
            setPollSelectedPollIntervalMinutes
          }
          inputMappings={pollInputMappings}
          onInputMappingsChange={setPollInputMappings}
          configurationValues={pollConfigurationValues}
          onConfigurationValuesChange={setPollConfigurationValues}
          triggerFieldGroups={pollTriggerFieldGroups}
          onTriggerFieldGroupsChange={setPollTriggerFieldGroups}
        />
      ) : isEmailTrigger ? (
        <EmailTriggerDialog
          isOpen={isDialogOpen}
          onOpenChange={handleEmailDialogOpenChange}
          inputSpecs={inputSpecs}
          automationId={automationId}
          isDraft={isDraft}
          isLoading={isUpdating || isUpdatingTriggerInstance}
          mode={isReadOnly ? 'readOnly' : 'edit'}
          initialData={getEmailInitialData()}
          triggerName={title}
          triggerStatus={triggerStatus}
          onToggleActiveState={handleToggleActiveState}
          onDelete={handleDeleteTrigger}
          onUpdate={handleEmailUpdate}
          currentStep={emailCurrentStep}
          onStepChange={setEmailCurrentStep}
          filters={emailFilters}
          onFiltersChange={setEmailFilters}
          inputMappings={emailInputMappings}
          onInputMappingsChange={setEmailInputMappings}
        />
      ) : (
        <WebhookTriggerDialog
          isOpen={isDialogOpen}
          onOpenChange={handleWebhookDialogOpenChange}
          inputSpecs={inputSpecs}
          isLoading={isUpdating || isUpdatingTriggerInstance}
          mode={isReadOnly ? 'readOnly' : 'edit'}
          initialData={getWebhookInitialData()}
          triggerName={title}
          triggerStatus={triggerStatus}
          onToggleActiveState={handleToggleActiveState}
          onDelete={handleDeleteTrigger}
          onUpdate={handleWebhookUpdate}
          // Controlled state props
          currentStep={webhookCurrentStep}
          onStepChange={setWebhookCurrentStep}
          selectedIntegration={webhookSelectedIntegration}
          onSelectedIntegrationChange={setWebhookSelectedIntegration}
          selectedIntegrationVersion={webhookSelectedIntegrationVersion}
          onSelectedIntegrationVersionChange={
            setWebhookSelectedIntegrationVersion
          }
          selectedConnection={webhookSelectedConnection}
          onSelectedConnectionChange={setWebhookSelectedConnection}
          selectedEvent={webhookSelectedEvent}
          onSelectedEventChange={setWebhookSelectedEvent}
          inputMappings={webhookInputMappings}
          onInputMappingsChange={setWebhookInputMappings}
          triggerFieldGroups={webhookTriggerFieldGroups}
          onTriggerFieldGroupsChange={setWebhookTriggerFieldGroups}
          configurationValues={webhookConfigurationValues}
          onConfigurationValuesChange={setWebhookConfigurationValues}
          filterConditions={webhookFilterConditions}
          onFilterConditionsChange={setWebhookFilterConditions}
        />
      )}

      {grimoireTriggerInfo.trigger_id && (
        <>
          <PauseTriggerDialog
            isOpen={isPauseDialogOpen}
            onClose={closePauseDialog}
            triggerType={
              isPoll
                ? TRIGGER_TYPE.POLL
                : isEmailTrigger
                  ? TRIGGER_TYPE.EMAIL
                  : TRIGGER_TYPE.WEBHOOK
            }
            organizationId={organizationId}
            workspaceId={workspaceId}
            automationId={automationId}
            triggerId={grimoireTriggerInfo.trigger_id}
            isDraft={isDraft}
            onSuccess={invalidateTriggerQueries}
          />
          <ResumeTriggerDialog
            isOpen={isResumeDialogOpen}
            onClose={closeResumeDialog}
            triggerType={
              isPoll
                ? TRIGGER_TYPE.POLL
                : isEmailTrigger
                  ? TRIGGER_TYPE.EMAIL
                  : TRIGGER_TYPE.WEBHOOK
            }
            organizationId={organizationId}
            workspaceId={workspaceId}
            automationId={automationId}
            triggerId={grimoireTriggerInfo.trigger_id}
            isDraft={isDraft}
            onSuccess={invalidateTriggerQueries}
          />
          <DeleteTriggerDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDeleteDialog}
            triggerType={
              isEmailTrigger ? TRIGGER_TYPE.EMAIL : TRIGGER_TYPE.WEBHOOK
            }
            organizationId={organizationId}
            workspaceId={workspaceId}
            automationId={automationId}
            triggerId={grimoireTriggerInfo.trigger_id}
            isDraft={isDraft}
            onSuccess={handleDeleteSuccess}
          />
        </>
      )}
    </>
  );
}
